import { Tooltip, TooltipContent, TooltipTrigger } from '@solinkcorp/solink-ui/components/Tooltip';
import { cn } from '@solinkcorp/solink-ui/lib/utils';
import { Info } from 'lucide-react';
import React, { type ReactNode, useCallback } from 'react';
import { SDM_EVENTS } from '../../constants';
import { useServiceDisplayDebuggerOpen } from '../../context/ServiceDisplayDebuggerContext';
import { useSDMAnalytics } from '../../contexts/AnalyticsContext';
import { useActiveDayPartLabel } from '../../hooks/useActiveDayPartLabel';
import { useValidationMessage } from '../../hooks/useValidationMessage';
import { useWiggleAnimation } from '../../hooks/useWiggleAnimation';
import { cardPositionIsInvalid } from '../../layout/utils';
import { type CommonWidgetProps } from '../../types';
import { useDarkMode } from '../DarkMode/useDarkMode';
import { getWidgetAnalytics } from './widgetAnalytics';
import { WidgetCardSkeletonOverlay } from './WidgetCardSkeletonOverlay';
import WidgetHeader from './WidgetHeader';

export interface WidgetCardProps extends CommonWidgetProps {
  className?: string;
  children: ReactNode;
  level: 'normal' | 'warning' | 'alert' | 'critical';
  title: string;
  /** debug label to show current threshold value for widget, fallback is the default widget threshold */
  thresholdDebugLabel?: string;
  /** start time of the horizon in ms to show when the debugger panel is open */
  horizonStartMs?: number | null;
  /** dependency to trigger animation on each value change. Used for drive-off widgets */
  wiggleDep?: number;
  /**
   * Float the title row and the validation footer over the content instead of stacking them
   * around it, so the content gets the whole card. The live feed uses this to give the video
   * every pixel: the header then lands in the letterbox bar when there is one, and over the
   * top of the video when there isn't.
   */
  overlayChrome?: boolean;
  /** Use a taller value area for large numeric/time displays */
  largeValue?: boolean;
  /** Render the title in the error colour (used when the card body shows an error state). */
  titleError?: boolean;
}

/** Get value text color based on alert level */
const getValueColor = (level: WidgetCardProps['level'], darkMode: boolean): string => {
  switch (level) {
    case 'warning':
      return darkMode ? 'text-amber-300' : 'text-amber-500';
    case 'alert':
    case 'critical':
      return darkMode ? 'text-red-400' : 'text-red-500';
    default:
      return '';
  }
};

const WidgetCard: React.FC<WidgetCardProps> = (props) => {
  const {
    controller,
    widget,
    hasConnectedTags,
    editing,
    hasMultipleAssignments,
    hasLine,
    hasZoneAndLine,
    horizonStartMs,
    tags,
    thresholdDebugLabel,
    aiTagName,
    isDataLoading,
    wiggleDep,
    overlayChrome,
    isDragging,
  } = props;
  const { id, position } = props.widget;

  const darkMode = useDarkMode();
  const analytics = useSDMAnalytics();

  const widgetKey = id ?? '';

  const configureWidget = useCallback(() => {
    analytics.trackSDMEvent(SDM_EVENTS.CONFIGURE_WIDGET, {
      description: 'Clicked configure widget',
      ...getWidgetAnalytics(widget, aiTagName),
    });
    controller.widgetEditStart(widgetKey);
  }, [controller, widgetKey, aiTagName, widget, analytics]);

  const deleteWidget = useCallback(() => {
    analytics.trackSDMEvent(SDM_EVENTS.DELETE_WIDGET, {
      description: 'Clicked delete widget',
      ...getWidgetAnalytics(widget, aiTagName),
    });
    controller.removeWidget(widgetKey);
  }, [controller, widgetKey, aiTagName, widget, analytics]);

  const { wiggleRef, wiggling, detectedDriveOff } = useWiggleAnimation(props.level, wiggleDep);
  const isInvalid = cardPositionIsInvalid(position);
  const isDebuggerOpen = useServiceDisplayDebuggerOpen();

  const validationMessage = useValidationMessage({
    widget,
    hasConnectedTags,
    editing,
    hasLine,
    hasMultipleAssignments,
    hasZoneAndLine,
    aiTagName,
  });

  const activeDayPartLabel = useActiveDayPartLabel(widget);

  return (
    <div className={cn('relative h-full w-full transition-all duration-300', wiggling && 'z-20')}>
      {/* Background layer */}
      <div
        ref={wiggleRef}
        className={cn(
          'absolute h-full w-full border border-border-muted bg-background-surface shadow-sm',
          isInvalid && 'border-2 border-destructive opacity-60',
        )}
        style={
          isInvalid
            ? {
                background: `repeating-linear-gradient(-45deg, var(--destructive), var(--destructive) 20px, var(--card) 20px, var(--card) 40px)`,
              }
            : undefined
        }
      />

      {/* Foreground layer */}
      <div
        className={cn(
          'relative flex h-full w-full flex-col items-center justify-center overflow-hidden text-center',
          'transition-all duration-200',
        )}
      >
        {/* Loading skeleton */}
        <WidgetCardSkeletonOverlay isDataLoading={isDataLoading ?? false} />

        {/* Title, tags and edit affordances */}
        <WidgetHeader
          title={props.title}
          titleError={props.titleError}
          tags={tags}
          editing={editing}
          isDragging={isDragging}
          overlay={overlayChrome}
          onConfigure={configureWidget}
          onDelete={deleteWidget}
        />

        {/* Value content, filling everything the header and footer don't need */}
        <div
          className={cn(
            'min-h-0 w-full flex-1',
            getValueColor(props.level, darkMode),
            wiggleDep && detectedDriveOff && getValueColor('critical', darkMode),
          )}
        >
          {props.children}
        </div>

        {/* Active day part and validation message */}
        <div
          className={cn(
            'flex flex-col items-center gap-1 pb-2',
            overlayChrome && 'absolute inset-x-0 bottom-0 z-10',
          )}
        >
          {activeDayPartLabel && (
            <div
              className="text-center font-semibold uppercase tracking-wider text-primary"
              data-testid="sdm-widget-active-day-part-label"
              style={{ fontSize: 'clamp(0.5rem, 1.8vh, 1rem)' }}
            >
              {activeDayPartLabel}
            </div>
          )}
          {/* Validation message or invisible placeholder for consistent spacing */}
          <div
            className={cn(
              'inline-flex items-center justify-center gap-1 px-3',
              !validationMessage && 'invisible',
            )}
          >
            <p className="m-0 break-words text-base leading-relaxed text-text-error opacity-80">
              {validationMessage?.text || '\u00A0'}
            </p>
            {validationMessage?.hasTooltip && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help">
                    <Info className="size-3.5 text-primary" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">{validationMessage.tooltipText}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Threshold debug - lower right (only when debugger panel is open) */}
        {isDebuggerOpen && thresholdDebugLabel != null && (
          <>
            {horizonStartMs !== undefined && (
              <div
                data-testid="sdm-widget-debug-horizon-start"
                className="absolute bottom-2 left-2 text-xs text-muted-foreground"
              >
                Horizon:{' '}
                {horizonStartMs === null
                  ? 'Real Time'
                  : new Date(horizonStartMs).toLocaleTimeString()}
              </div>
            )}
            <div
              className="absolute bottom-2 right-2 text-xs text-muted-foreground"
              data-testid="sdm-widget-debug-threshold"
            >
              Threshold: {thresholdDebugLabel}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default React.memo(WidgetCard);
